import { useEffect, useState } from 'react'
import { CouvertureDto } from '../../common/interfaces'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks'
import { setReaderId } from '../../state/reducers/patientsReducer'
import { getCartVitalUrl } from '../../state/thunks/cardReadThunk'
import {
  CardReaderDto,
  getUrl,
  sendReaderResponse,
} from '../../services/CardReadService'
import { enqueueSnackbar } from '../../state/reducers/alertReducer'
import QRCodeReaderComponent from '../QRCodeReaderComponent'
import { isBase64 } from '../../utils/dto.mapper'
import {
  Alert,
  Box,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { Dialog } from '@mui/material'
import { BillingService } from '../../services/BillingService'

type Props = {
  isOpen: boolean
  onFinish: (dto: CouvertureDto) => void
  visitId: number
  readDate: string
  onClose: () => void
}

const APcvQRCodeReaderComponent: React.FC<Props> = ({
  isOpen,
  onFinish,
  visitId,
  readDate,
  onClose,
}) => {
  const dispatch = useAppDispatch()

  const [url, setUrl] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const { memberSituation } = useAppSelector((state) => ({
    memberSituation: state.order.selectedMemberSituation?.situation,
  }))


  useEffect(() => {
    if (!isOpen) {
      if (url) {
        setUrl('')
      }
      return
    }
    dispatch(getCartVitalUrl())
      .unwrap()
      .then(async (dto) => {
        dispatch(setReaderId(dto.id))
        handleOpenQRCodeReader()
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: 'Erreur lors de la détection du lecteur',
            options: { variant: 'error' },
          }),
        )
        onClose()
      })
  }, [dispatch, isOpen, memberSituation])



  useEffect(() => {
    const handlePostBack = async (msg: MessageEvent) => {
      const data = msg.data
      try {
        if (!isBase64(data)) {
          return
        }

        const decodedStr = atob(data)
        const obj = JSON.parse(decodedStr)
        if (!obj.method) {
          dispatch(
            enqueueSnackbar({
              message: 'Erreur de lecture',
              options: { variant: 'error' },
            }),
          )
          onClose()
          return
        }
        if (obj.method.cancel) {
          onClose()
          return
        }
        const dto = await sendReaderResponse(visitId, readDate, obj)

        if (!dto) {
          dispatch(
            enqueueSnackbar({
              message: 'Erreur de lecture',
              options: { variant: 'error' },
            }),
          )
          onClose()
          return
        }
        onFinish(dto)
        onClose()
      } catch (error) {
        dispatch(
          enqueueSnackbar({
            message: 'Erreur de lecture',
            options: { variant: 'error' },
          }),
        )
        onClose()
      }
    }
    window.addEventListener('message', handlePostBack, false)
    return () => {
      window.removeEventListener('message', handlePostBack)
    }
  }, [visitId, readDate])

  const handleCloseQrCodeReader = () => {
    setDialogOpen(false)
  }

  const handleOpenQRCodeReader = () => {
    setDialogOpen(true)
  }


  const handleQRCodeDetected = async (qrCode: string) => {
    handleCloseQrCodeReader()
    let readerDto: CardReaderDto
    try {
      readerDto = await getUrl()
      if (!readerDto) {
        dispatch(
          enqueueSnackbar({
            message: 'Erreur lors de la récupération de l\'URL',
            options: { variant: 'error' },
          }),
        )
        return
      }
      if (!memberSituation) {
        dispatch(
          enqueueSnackbar({
            message: 'Situation du médecin manquante',
            options: { variant: 'error' },
          }),
        )
        return
      }
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: 'Erreur lors de la récupération de l\'URL',
          options: { variant: 'error' },
        }),
      )
      return
    }
    try {
      const data = await BillingService.getUrlByQRCode({
        idLecteur: readerDto.id,
        idResip: memberSituation?.idJfse ?? 1,
        dateLecture: readDate,
        typeLecture: 1,
        qrCode,
      })
      if (data.erreur) {
        dispatch(
          enqueueSnackbar({
            message: data.erreurMessage,
            options: { variant: 'error' },
          }),
        )
        return
      }
      setUrl(data.url)
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'Erreur de lecture',
          options: { variant: 'error' },
        }),
      )
      onClose()
    }
  }

  const onIframClose = () => {
    setUrl('')
    onClose()
  }

  return (
    <div>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={dialogOpen}
        onClose={() => {
          handleCloseQrCodeReader()
          onClose()
        }}
      >
        {memberSituation && (
          <QRCodeReaderComponent
            open={dialogOpen}
            onClose={() => {
              handleCloseQrCodeReader()
              onClose()
            }}
            onQRCodeDetected={handleQRCodeDetected}
          />
        )}
      </Dialog>

      <Dialog
        open={!!url}
        onClose={onIframClose}
        className="z-[100000]"
        maxWidth="lg"
        aria-labelledby="form-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 5,
            background: '#E2E6F2',
            zIndex: 100001,
          },
        }}
      >
        <DialogTitle
          style={{
            background: '#fff',
            color: '#000',
          }}
        >
          Lecture de la carte vitale
        </DialogTitle>
        <DialogContent
          style={{
            minWidth: '850px',
            display: 'flex',
            justifyContent: 'center',
            height: '80vh',
            alignItems: 'center',

            zIndex: 100000001,
          }}
        >
          {url === '' ? (
            <CircularProgress className="!text-purple-600" />
          ) : (
            <iframe
              style={{
                width: '100%',
                height: '80vh',
                border: 'none',
                overflow: 'hidden',
                marginTop: '20px',
              }}
              src={url}
            ></iframe>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default APcvQRCodeReaderComponent
